import { useState, useEffect } from 'react';
import {
  MessageSquare, Search, Plus, Trash2, 
  Loader2, AlertTriangle, Settings, Sparkles,
  Users, Bot
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { agentService } from '../../services/PlatformService';
import { formatDate } from '../../lib/dateHelpers';
// Remover temporariamente o AssistantPopup para testar
import AssistantPopup from '../../components/popup/AssistantPopup';
import { getAgentAvatar } from '../../lib/formatters';

const UserAgents = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Função de busca sem useCallback para evitar loops
  const fetchAgents = async () => {
    if (isDeleting) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        search: searchTerm || undefined,
        status: selectedFilter !== 'all' ? selectedFilter : undefined,
        order: "DESC"
      };

      const response = await agentService.getUserAgents(params);
      
      // Garantir que sempre temos um array
      const agentsData = response?.data || [];
      setAgents(Array.isArray(agentsData) ? agentsData : []);
    } catch (err) {
      console.error("Erro ao buscar agentes:", err);
      setError(err.response?.data?.detail || err.message || "Não foi possível carregar os agentes.");
      setAgents([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Carregamento inicial apenas
  useEffect(() => {
    fetchAgents();
  }, []); // Array vazio - executa apenas uma vez

  // Busca com debounce quando searchTerm ou selectedFilter mudam
  useEffect(() => {
    if (searchTerm === '' && selectedFilter === 'all') return;
    
    const timeoutId = setTimeout(() => {
      fetchAgents();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedFilter]);

  const handleDeleteAgent = async (agentId, agentName) => {
    if (isDeleting || isLoading) return;

    const confirmDelete = window.confirm(
      `Tem certeza que deseja demitir "${agentName}"? Esta ação não pode ser desfeita.`
    );
    
    if (!confirmDelete) return;

    setIsDeleting(true);

    try {
      await agentService.deleteAgent(agentId);
      
      // Remove o agente da lista local
      setAgents(prevAgents => prevAgents.filter(agent => agent.id !== agentId));
      
      alert(`"${agentName}" foi desligado da equipe!`);
    } catch (err) {
      console.error(`Erro ao excluir agente ${agentId}:`, err);
      const errorMessage = err.response?.data?.detail || err.message || "Não foi possível excluir o agente.";
      alert(`Erro ao excluir: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const getAgentStatusBadge = (status) => {
    if (status === 'public') {
      return (
        <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg">
          Online
        </span>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      {/* Header Gradiente */}
      <div className="bg-gradient-to-r from-green-800 via-green-600 to-green-600 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
                <Users className="w-10 h-10" />
                Sua Equipe Digital
              </h1>
              <p className="text-xl opacity-90">
                Gerencie seus funcionários virtuais que trabalham 24/7
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-lg font-semibold bg-white/20 px-4 py-2 rounded-full">
                {agents.length} {agents.length === 1 ? 'Agente' : 'Agentes'} Ativos
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Barra de Ações */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 mb-8 border border-gray-100">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-12 pr-6 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all"
                  placeholder="Buscar funcionários..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-tutorial="search-agents"
                />
              </div>
            </div>

            <div className="flex gap-3 items-center">
              <select
                className="px-6 py-4 text-lg font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-2xl hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 transition-all cursor-pointer"
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                data-tutorial="filter-agents"
              >
                <option value="all">Todos</option>
              </select>

              <Link
                to="/agent-manager"
                className="inline-flex items-center px-6 py-4 bg-gradient-to-r from-green-800 to-green-600 rounded-2xl text-white text-lg font-semibold hover:from-green-700 hover:to-green-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg gap-3"
                data-tutorial="create-agent"
              >
                <Plus className="w-6 h-6" />
                Contratar Agente
              </Link>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-green-100 rounded-3xl mb-4">
              <Loader2 className="h-10 w-10 text-green-600 animate-spin" />
            </div>
            <p className="text-xl text-gray-600">Carregando sua equipe...</p>
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-5 rounded-2xl shadow-lg flex items-center mb-8">
            <AlertTriangle className="h-8 w-8 mr-4 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-lg">Ops! Algo deu errado</p>
              <p className="text-base mt-1">{error}</p>
            </div>
            <button 
              onClick={() => fetchAgents()} 
              className="ml-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Agentes Grid */}
        {!isLoading && agents.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-tutorial="agents-grid">
            {agents.map(agent => (
              <div 
                key={agent.id} 
                className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 hover:shadow-2xl transition-all transform hover:scale-[1.02] border border-gray-100"
              >
                <div className="text-center">
                  {/* Avatar do Agente */}
                  <div className="relative inline-block mb-4">
                    <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-indigo-100 to-green-100 p-1">
                      <div className="w-full h-full rounded-full overflow-hidden bg-white">
                        <img
                          src={getAgentAvatar(agent)}
                          alt={agent.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    {getAgentStatusBadge(agent.status)}
                  </div>

                  {/* Nome e Descrição */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{agent.name}</h3>
                  <p className="text-base text-gray-600 mb-4 line-clamp-2 min-h-[3rem]">
                    {agent.description || 'Funcionário digital pronto para ajudar!'}
                  </p>

                  {/* Tags */}
                  <div className="flex justify-center gap-2 mb-4 flex-wrap">
                    {agent.tags && agent.tags.length > 0 && (
                      <span className="bg-gradient-to-r from-indigo-100 to-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                        {agent.tags[0]}
                      </span>
                    )}
                    <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                      Desde {formatDate(agent.created_at)}
                    </span>
                  </div>

                  {/* Ações */}
                  <div className="flex gap-2">
                    <Link 
                      to={`/agent-chat?id=${agent.id}`} 
                      className="flex-1 py-3 px-4 bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl hover:from-green-700 hover:to-green-700 transition-all flex items-center justify-center gap-2 font-semibold"
                      data-tutorial="chat-agent"
                    >
                      <MessageSquare className="w-5 h-5" />
                      Conversar
                    </Link>
                    <Link 
                      to={`/agent-manager?id=${agent.id}`} 
                      className="p-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-100 transition-all"
                      title="Configurar"
                      data-tutorial="manage-agent"
                    >
                      <Settings className="w-5 h-5" />
                    </Link>
                    <button
                      className="p-3 border-2 border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-all disabled:opacity-50"
                      onClick={() => handleDeleteAgent(agent.id, agent.name)}
                      title="Demitir funcionário"
                      data-tutorial="delete-agent"
                      disabled={isDeleting}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Card de Adicionar Novo */}
            <Link 
              to="/agent-manager"
              className="bg-white/50 backdrop-blur-sm rounded-3xl shadow-lg p-6 hover:shadow-xl transition-all transform hover:scale-[1.02] border-2 border-dashed border-indigo-300 flex flex-col items-center justify-center min-h-[400px] group"
            >
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-indigo-100 to-green-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Plus className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-green-600 mb-2">Contratar Novo</h3>
              <p className="text-base text-gray-600 text-center">
                Adicione mais um funcionário à sua equipe digital
              </p>
            </Link>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && agents.length === 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-16 text-center">
            <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-indigo-100 to-green-100 rounded-full mb-6">
              <Bot className="h-16 w-16 text-green-600" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Sua equipe está vazia! 😢
            </h3>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {searchTerm || selectedFilter !== 'all' 
                ? 'Nenhum funcionário encontrado com esses filtros.' 
                : 'Comece contratando seu primeiro funcionário digital. Eles trabalham 24/7 sem reclamar!'}
            </p>
            <Link
              to="/agent-manager"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-800 to-green-600 rounded-2xl text-white text-xl font-bold hover:from-green-700 hover:to-green-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg gap-3"
            >
              <Sparkles className="h-7 w-7" />
              Contratar Primeiro Funcionário
            </Link>
          </div>
        )}
      </div>

      {/* Assistente Popup - Comentado temporariamente */}
       <AssistantPopup /> 
    </div>
  );
};

export default UserAgents;